import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, User, Linkedin, Twitter, Facebook, Mail, ArrowUpRight } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { api, resolveImageUrl } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blogs/${slug}`, { params: { destination: "journal" } });
        setData(data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
    window.scrollTo(0, 0);
  }, [slug]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try { await axios.post(`${API}/subscribe`, { email }); toast.success("Subscribed. Look out for the next Insight."); setEmail(""); }
    catch { toast.error("Subscription failed. Please try again."); }
    finally { setSubscribing(false); }
  };

  if (loading) return <div className="grid min-h-[60vh] place-items-center font-body text-slate-500">Loading…</div>;
  if (!data) return (
    <div className="grid min-h-[60vh] place-items-center text-center">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy">Article not found</h1>
        <p className="mt-2 font-body text-slate-500">This Executive Insight may have been moved or unpublished.</p>
        <button onClick={() => nav("/journal")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </button>
      </div>
    </div>
  );

  const { blog, prev, next, related } = data;
  const canonical = `https://aftabazad.com/journal/${blog.slug}`;
  const heroFull = resolveImageUrl(blog.hero_image);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    description: blog.meta_description || blog.excerpt || undefined,
    image: heroFull || undefined,
    datePublished: blog.published_at,
    dateModified: blog.updated_at,
    author: { "@type": "Person", name: blog.author || "Aftab Azad" },
    publisher: { "@type": "Person", name: "Aftab Azad" },
    mainEntityOfPage: canonical,
    articleSection: blog.category,
    keywords: (blog.tags || []).join(", "),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://aftabazad.com/" },
      { "@type": "ListItem", position: 2, name: "Executive Journal", item: "https://aftabazad.com/insights" },
      { "@type": "ListItem", position: 3, name: blog.title, item: canonical },
    ],
  };

  const encoded = encodeURIComponent(canonical);
  const encodedTitle = encodeURIComponent(blog.title);
  const share = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
    twitter: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
    email: `mailto:?subject=${encodedTitle}&body=${encoded}`,
  };

  return (
    <>
      <Seo
        title={blog.meta_title || blog.title}
        description={blog.meta_description || blog.excerpt || `${blog.title} — an Executive Insight by Aftab Azad.`}
        path={`/journal/${blog.slug}`}
        keywords={blog.tags || []}
        type="article"
        image={heroFull || undefined}
        jsonLd={{ "@context": "https://schema.org", "@graph": [articleLd, breadcrumbLd] }}
      />

      <article>
        <header className="bg-navy pt-32 pb-14 text-white grain-overlay md:pt-40 md:pb-16">
          <div className="mx-auto max-w-3xl px-6 md:px-12">
            <nav className="mb-6 font-body text-xs text-slate-400">
              <Link to="/" className="hover:text-sky">Home</Link> <span className="mx-1">/</span>
              <Link to="/journal" className="hover:text-sky">Executive Journal</Link>
            </nav>
            <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">{blog.category}</p>
            <h1 data-testid="journal-title" className="mt-4 font-heading text-3xl font-light leading-[1.1] tracking-tight md:text-5xl">{blog.title}</h1>
            {blog.excerpt && <p className="mt-5 font-body text-lg leading-relaxed text-slate-300 md:text-xl">{blog.excerpt}</p>}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-body text-sm text-slate-400">
              <span className="inline-flex items-center gap-2"><User className="h-4 w-4" /> {blog.author}</span>
              {blog.published_at && <span>{format(new Date(blog.published_at), "d MMMM yyyy")}</span>}
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {blog.reading_time} min read</span>
            </div>
          </div>
        </header>

        {heroFull && (
          <div className="bg-white">
            <div className="mx-auto -mt-10 max-w-4xl px-6 md:px-12">
              <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 shadow-xl">
                <img src={heroFull} alt={blog.title} className="w-full object-cover" />
              </div>
            </div>
          </div>
        )}

        <section className="bg-white py-14">
          <div className="mx-auto max-w-3xl px-6 md:px-12">
            <div className="mb-8 flex flex-wrap items-center gap-2 border-y border-slate-200 py-4">
              <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Share</span>
              <ShareBtn href={share.linkedin} label="LinkedIn"><Linkedin className="h-4 w-4" /></ShareBtn>
              <ShareBtn href={share.twitter} label="X"><Twitter className="h-4 w-4" /></ShareBtn>
              <ShareBtn href={share.facebook} label="Facebook"><Facebook className="h-4 w-4" /></ShareBtn>
              <ShareBtn href={share.email} label="Email" newTab={false}><Mail className="h-4 w-4" /></ShareBtn>
            </div>

            <div data-testid="journal-content" className="prose prose-lg prose-slate max-w-none prose-headings:font-heading prose-headings:tracking-tight prose-h2:text-3xl prose-h3:text-2xl prose-a:text-royal prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:border prose-img:border-slate-200" dangerouslySetInnerHTML={{ __html: blog.content || "" }} />

            {(blog.tags || []).length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2 border-t border-slate-200 pt-6">
                {blog.tags.map((t) => (
                  <span key={t} className="rounded-full bg-navy/5 px-3 py-1 font-body text-xs font-semibold text-navy">#{t}</span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-surface py-12">
          <div className="mx-auto flex max-w-3xl flex-wrap justify-between gap-6 px-6 md:px-12">
            {prev ? (
              <Link to={`/journal/${prev.slug}`} className="group flex-1 min-w-[240px] rounded-[1rem] border border-slate-200 bg-white p-5 hover:border-royal">
                <span className="inline-flex items-center gap-1 font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-500"><ArrowLeft className="h-3 w-3" /> Previous</span>
                <p className="mt-2 font-heading font-semibold text-navy group-hover:text-royal">{prev.title}</p>
              </Link>
            ) : <div className="flex-1 min-w-[240px]" />}
            {next ? (
              <Link to={`/journal/${next.slug}`} className="group flex-1 min-w-[240px] rounded-[1rem] border border-slate-200 bg-white p-5 text-right hover:border-royal">
                <span className="inline-flex items-center gap-1 font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Next <ArrowRight className="h-3 w-3" /></span>
                <p className="mt-2 font-heading font-semibold text-navy group-hover:text-royal">{next.title}</p>
              </Link>
            ) : <div className="flex-1 min-w-[240px]" />}
          </div>
        </section>

        {related?.length > 0 && (
          <section className="bg-white py-16 md:py-20">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Related insights</p>
              <h2 className="mt-3 font-heading text-3xl font-light tracking-tight text-navy md:text-4xl">Read next.</h2>
              <div className="mt-10 grid gap-8 md:grid-cols-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/journal/${r.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white hover:border-royal">
                    {r.hero_image ? (
                      <div className="h-40 overflow-hidden bg-slate-100"><img src={resolveImageUrl(r.hero_image)} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                    ) : (
                      <div className="flex h-40 items-center justify-center bg-navy font-heading text-lg text-white/40">{r.category}</div>
                    )}
                    <div className="flex flex-1 flex-col p-5">
                      <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-royal">{r.category}</p>
                      <h3 className="mt-2 font-heading text-lg font-bold leading-snug tracking-tight text-navy group-hover:text-royal">{r.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="bg-surface py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive newsletter</p>
            <h2 className="mt-3 font-heading text-3xl font-light tracking-tight text-navy md:text-4xl">Get the next Insight in your inbox.</h2>
            <p className="mt-3 font-body text-slate-600">One considered email. No noise, ever.</p>
            <form onSubmit={subscribe} className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full max-w-sm rounded-full border border-slate-200 bg-white px-5 py-3 font-body text-sm outline-none focus:border-royal" />
              <button disabled={subscribing} className="inline-flex items-center gap-2 rounded-full bg-royal px-6 py-3 font-body text-sm font-semibold text-white hover:bg-navy disabled:opacity-60">
                {subscribing ? "Subscribing…" : "Subscribe"} <ArrowUpRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </section>
      </article>

      <CTASection
        overline="Ready to talk?"
        heading={["Book an executive", "strategy session."]}
        body="A confidential conversation about your business priorities, your technology decisions and the next stage of growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}

function ShareBtn({ href, label, children, newTab = true }) {
  return <a href={href} target={newTab ? "_blank" : undefined} rel={newTab ? "noreferrer" : undefined} aria-label={label} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-royal hover:text-royal">{children}</a>;
}
